import { quat, vec3 } from "gl-matrix";
import { useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { getSurfaceProjection } from "features/render/surfaceProjection";
import { isRealVec } from "utils/misc";

import { calculateReferenceDistances } from "./markerScaling";
import {
    selectAllCollections,
    selectCreationCollectionId,
    selectCreationPreviewPosition,
    selectEditPreviewPosition,
    selectIsCreatingMarker,
    selectIsEditingMarkerLocation,
    selectPlacementNormal,
    selectSelectedMarker,
} from "./markersSlice";
import { getPlacementDiscScreenRadius, PLACEMENT_HIGHLIGHT } from "./placementHighlightUtils";

export const PLACEMENT_HIGHLIGHT_TEST_ID = "marker-placement-highlight";

// The white outline's outer edge is at 1.197 x fill radius; pad slightly beyond it.
const VIEWBOX_EXTENT = 1.25;

/**
 * Surface-aligned circular highlight (disc) rendered under the pin tip while
 * placing a new pin or dragging an existing one.
 *
 * The disc consists of a translucent green fill separated by a small gap from
 * a semi-transparent white outline. It is positioned at the picked surface point
 * and tilted to follow the plane of the surface via a rotate3d transform
 * derived from the view-space surface normal. Its size tracks the
 * distance-scaled pin preview so the two stay visually locked while zooming.
 *
 * Enabled per collection via {@link MarkerCollectionConfig.showPlacementHighlight}.
 * Rendered below the markers SVG so pins draw on top of the disc.
 */
export function MarkerPlacementHighlight() {
    const {
        state: { view, size, internalEventEmitter },
    } = useExplorerGlobals();
    const collections = useAppSelector(selectAllCollections);
    const isCreatingMarker = useAppSelector(selectIsCreatingMarker);
    const creationCollectionId = useAppSelector(selectCreationCollectionId);
    const creationPreviewPosition = useAppSelector(selectCreationPreviewPosition);
    const isEditingMarkerLocation = useAppSelector(selectIsEditingMarkerLocation);
    const editPreviewPosition = useAppSelector(selectEditPreviewPosition);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const placementNormal = useAppSelector(selectPlacementNormal);

    // Re-project on camera movement so the disc stays glued to the surface
    // point while zooming/orbiting with a stationary pointer.
    const [, setCameraTick] = useState(0);
    const rafIdRef = useRef<number | null>(null);

    // Resolve the active placement interaction: dragging an existing marker
    // takes precedence over the creation preview (only one runs at a time).
    let worldPosition: vec3 | null = null;
    let activeCollectionId: string | null = null;
    if (isEditingMarkerLocation && editPreviewPosition && selectedMarker) {
        worldPosition = editPreviewPosition;
        activeCollectionId = selectedMarker.collectionId;
    } else if (isCreatingMarker && creationPreviewPosition && creationCollectionId) {
        worldPosition = creationPreviewPosition;
        activeCollectionId = creationCollectionId;
    }

    const isEnabled = collections[activeCollectionId ?? ""]?.config.showPlacementHighlight === true;
    const isActive =
        isEnabled && worldPosition !== null && placementNormal !== null && Boolean(view) && isRealVec(placementNormal);

    useEffect(() => {
        // The engine emits camera events from its render loop. Subscribe only
        // while a visible placement disc needs reprojection; at all other
        // times this component is intentionally inert.
        if (!isActive || !internalEventEmitter) {
            return;
        }

        const scheduleTick = () => {
            if (rafIdRef.current !== null) {
                return;
            }
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                setCameraTick((tick) => tick + 1);
            });
        };

        const unsubscribeCameraUpdated = internalEventEmitter.on("cameraUpdated", scheduleTick);
        const unsubscribeCameraSettled = internalEventEmitter.on("cameraSettled", scheduleTick);

        return () => {
            unsubscribeCameraUpdated();
            unsubscribeCameraSettled();
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [internalEventEmitter, isActive]);

    if (!isEnabled || !worldPosition || !placementNormal || !view || !isRealVec(placementNormal)) {
        return null;
    }

    const screenPoints = view.convert.worldSpaceToScreenSpace([worldPosition], { round: false });
    const screenPos = screenPoints?.[0];
    if (!screenPos) {
        return null;
    }
    const [x, y] = screenPos;

    const camera = view.renderState.camera;
    const distance = vec3.distance(camera.position, worldPosition);
    // Same scene-adaptive reference distances the pin renderer uses, so the
    // disc scales and clamps in lockstep with the pin.
    const referenceDistances = calculateReferenceDistances(view.renderState.scene?.config.boundingSphere?.radius);
    const radius = getPlacementDiscScreenRadius(distance, referenceDistances);
    if (radius <= 0) {
        return null;
    }

    // Derive the view-space normal from the stored world-space normal so the
    // tilt stays correct when the camera rotates between picks. camera.rotation
    // is camera->world; its conjugate maps world->view.
    const worldToView = quat.conjugate(quat.create(), camera.rotation);
    const normalVS = vec3.transformQuat(vec3.create(), placementNormal, worldToView);
    if (vec3.squaredLength(normalVS) < 1e-12) {
        // Degenerate normal (e.g. zero vector from an edge/corner sample) — no
        // meaningful surface plane to align to.
        return null;
    }
    vec3.normalize(normalVS, normalVS);
    // Flip the normal toward the camera (view space looks down -Z, so a
    // camera-facing surface has a positive Z normal) to handle grazing hits
    // on back-facing geometry.
    if (normalVS[2] < 0) {
        vec3.negate(normalVS, normalVS);
    }

    const surfaceTransform = getSurfaceProjection(normalVS, x, y, size, view)?.transform ?? "";

    const svgExtent = 2 * VIEWBOX_EXTENT * radius;
    const { color, fillOpacity, outlineOpacity, outlineInner, outlineOuter } = PLACEMENT_HIGHLIGHT;

    return (
        <div
            data-testid={PLACEMENT_HIGHLIGHT_TEST_ID}
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 0,
                height: 0,
                pointerEvents: "none",
                transform: `translate3d(${x}px, ${y}px, 0) ${surfaceTransform}`.trim(),
            }}
        >
            <svg
                width={svgExtent}
                height={svgExtent}
                viewBox={`${-VIEWBOX_EXTENT} ${-VIEWBOX_EXTENT} ${2 * VIEWBOX_EXTENT} ${2 * VIEWBOX_EXTENT}`}
                style={{
                    position: "absolute",
                    left: -svgExtent / 2,
                    top: -svgExtent / 2,
                    pointerEvents: "none",
                }}
            >
                <circle r={1} fill={color} fillOpacity={fillOpacity} />
                <circle
                    r={(outlineInner + outlineOuter) / 2}
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth={outlineOuter - outlineInner}
                    strokeOpacity={outlineOpacity}
                />
            </svg>
        </div>
    );
}
